import clsx from 'clsx';

import type { CellRangeRowSlice, CellRangeSelection, CopiedOutline, Position } from '../types';
import {
  cellCopiedBottomClassname,
  cellCopiedLeftClassname,
  cellCopiedRangeClassname,
  cellCopiedRightClassname,
  cellCopiedTopClassname,
  cellRangeBottomClassname,
  cellRangeLeftClassname,
  cellRangeRightClassname,
  cellRangeSelectedClassname,
  cellRangeTopClassname,
} from '../style/cell.css';

const { min, max } = Math;

export function getNormalizedRange(anchor: Position, head: Position): CellRangeSelection {
  return {
    startIdx: min(anchor.idx, head.idx),
    startRowIdx: min(anchor.rowIdx, head.rowIdx),
    endIdx: max(anchor.idx, head.idx),
    endRowIdx: max(anchor.rowIdx, head.rowIdx),
  };
}

export function isPositionWithinBounds({ idx, rowIdx }: Position, maxColIdx: number, rowsLength: number): boolean {
  return idx >= 0 && idx <= maxColIdx && rowIdx >= 0 && rowIdx < rowsLength;
}

/**
 * Resolves a key-addressed copied outline to an idx-space range against the
 * grid's current row/column key order. `null` once a re-sort or filter breaks
 * the keys' contiguity — the outline silently disappears.
 */
export function resolveCopiedRange(
  outline: CopiedOutline,
  rowKeys: readonly string[],
  columnKeys: readonly string[],
): CellRangeSelection | null {
  const rowRun = resolveContiguousRun(outline.rowKeys, rowKeys);
  const columnRun = resolveContiguousRun(outline.columnKeys, columnKeys);
  if (rowRun === null || columnRun === null) return null;
  return {
    startIdx: columnRun.start,
    endIdx: columnRun.end,
    startRowIdx: rowRun.start,
    endRowIdx: rowRun.end,
  };
}

function resolveContiguousRun(
  keys: readonly string[],
  orderedKeys: readonly string[],
): { start: number; end: number } | null {
  if (keys.length === 0) return null;
  const indexByKey = new Map(orderedKeys.map((key, index) => [key, index]));
  const start = indexByKey.get(keys[0]);
  if (start === undefined) return null;
  for (let i = 1; i < keys.length; i++) {
    if (indexByKey.get(keys[i]) !== start + i) return null;
  }
  return { start, end: start + keys.length - 1 };
}

export interface CellEdgeClassnames {
  readonly base: string;
  readonly top: string;
  readonly bottom: string;
  readonly left: string;
  readonly right: string;
}

/**
 * Classes for a cell's part of a rectangular highlight (selection range or copied
 * range), or `undefined` when the row has no active slice or the cell's occupied
 * span `[idx, idx + colSpan - 1]` doesn't intersect it. A colspan cell
 * intersecting the rectangle is highlighted across its whole span, while the
 * logical range stays in idx-space. Perimeter edges are turned on via the
 * per-edge classes so any combination composes.
 */
export function getCellEdgeClassname(
  idx: number,
  colSpan: number,
  slice: CellRangeRowSlice | null,
  classes: CellEdgeClassnames,
): string | undefined {
  if (slice === null) return undefined;
  const { startIdx, endIdx, isTopRow, isBottomRow } = slice;
  const cellEndIdx = idx + colSpan - 1;
  if (cellEndIdx < startIdx || idx > endIdx) return undefined;
  return clsx(classes.base, {
    [classes.left]: idx <= startIdx,
    [classes.right]: cellEndIdx >= endIdx,
    [classes.top]: isTopRow,
    [classes.bottom]: isBottomRow,
  });
}

const rangeEdgeClasses: CellEdgeClassnames = {
  base: cellRangeSelectedClassname,
  top: cellRangeTopClassname,
  bottom: cellRangeBottomClassname,
  left: cellRangeLeftClassname,
  right: cellRangeRightClassname,
};

const copiedEdgeClasses: CellEdgeClassnames = {
  base: cellCopiedRangeClassname,
  top: cellCopiedTopClassname,
  bottom: cellCopiedBottomClassname,
  left: cellCopiedLeftClassname,
  right: cellCopiedRightClassname,
};

/** Selection-range highlight for a cell. */
export function getRangeCellClassname(
  idx: number,
  colSpan: number,
  slice: CellRangeRowSlice | null,
): string | undefined {
  return getCellEdgeClassname(idx, colSpan, slice, rangeEdgeClasses);
}

/** Copied-range (marching-ants) highlight for a cell. */
export function getCopiedCellClassname(
  idx: number,
  colSpan: number,
  slice: CellRangeRowSlice | null,
): string | undefined {
  return getCellEdgeClassname(idx, colSpan, slice, copiedEdgeClasses);
}
